import { z } from 'zod';
import { ORG_TYPES } from '../enums';
import { slugSchema, timestampMsSchema, uuidSchema } from './common';

/** Public-facing shape of an organization row. Never includes internal columns.
 *  Vibe Space fields (tagline, aboutHtml, accentColor, heroEmoji,
 *  vibeSpaceVisible) added in migration 0010 — all optional/nullable so
 *  pre-existing orgs validate without backfill. */
export const organizationShape = z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string().min(1).max(120),
  type: z.enum(ORG_TYPES),
  logoR2Key: z.string().nullable(),
  bannerR2Key: z.string().nullable(),
  parentOrgId: uuidSchema.nullable(),
  tagline: z.string().nullable().optional(),
  aboutHtml: z.string().nullable().optional(),
  accentColor: z.string().nullable().optional(),
  heroEmoji: z.string().nullable().optional(),
  vibeSpaceVisible: z.boolean().optional(),
  createdAt: timestampMsSchema,
  updatedAt: timestampMsSchema,
});
export type OrganizationShape = z.infer<typeof organizationShape>;

/** Payload for creating an organization at signup or via the org-admin panel. */
export const createOrganizationRequest = z.object({
  slug: slugSchema,
  name: z.string().min(1).max(120),
  type: z.enum(ORG_TYPES),
  parentOrgId: uuidSchema.nullable().optional(),
});
export type CreateOrganizationRequest = z.infer<typeof createOrganizationRequest>;

/** Payload for renaming / rebranding. Slug intentionally NOT editable here — it's
 *  a stable URL surface and changing it breaks Vibe Space links. Slug rename has
 *  its own audit-logged endpoint. */
export const updateOrganizationRequest = z.object({
  name: z.string().min(1).max(120).optional(),
  logoR2Key: z.string().nullable().optional(),
  bannerR2Key: z.string().nullable().optional(),
});
export type UpdateOrganizationRequest = z.infer<typeof updateOrganizationRequest>;
